import { useState } from 'react';
import { isEmpty, isFunction } from 'lodash-es';
import { useAsyncEffect } from 'ahooks';
import useSearchParams from '@gp/hooks/useSearchParams';
import { fetchShortUrlParams, updateShortUrlParams } from '@gp/services/filter';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { getValidGranularity, getValidDateRange } from '@gp/utils/date';
import { getValidValue, getValidListValue } from '@gp/utils/validateData';
import type { IRecord, IShortUrlParams } from '@gp/types/global';
import {
  getFilterStorageValue,
  getFilterSaveValue,
  getFilterSaveSelectedValue,
} from '@gp/utils/utils';
import { getSaveValue } from '@gp/components/FilterCommon/components/FilterSave/util';

interface IConditions {
  tab?: string[];
  segment?: string[];
  filter: IRecord;
}

interface IValues {
  tab?: string;
  segment?: string;
  filter: IRecord;
}

interface IStoreValues extends IValues {
  lock: boolean;
}

const deserializeShortUrlParams = (params?: string): IShortUrlParams => {
  if (!params) return { filter: {} };
  try {
    const { filter, ...restParams } = JSON.parse(params);
    return { ...restParams, filter: filter || {} };
  } catch (exception) {
    return { filter: {} };
  }
};

const getValidFilter = (params?: {
  tab?: string;
  segment?: string;
  filter?: IRecord;
  filterConditions?: IRecord;
  filterDefaultValues?: IRecord;
  allFilterOptionsValues?: any;
}): IRecord => {
  const { tab, segment, filter, filterConditions, filterDefaultValues, allFilterOptionsValues } =
    params || {};
  if (!filterDefaultValues) return {};
  if (!filterConditions) return filterDefaultValues;
  const result: IRecord = {};
  Object.keys(filterDefaultValues).forEach((key) => {
    const filterValue = filter?.[key];
    const filterCondition = filterConditions?.[key];
    const filterDefaultValue = filterDefaultValues?.[key];
    const allFilterOptionsValue = allFilterOptionsValues?.[key];

    if (key === 'composedDatePicker') {
      const granularities = ['month', 'quarter', 'year'];
      const { range: ranges, defaultDate: defaultDates } =
        (isFunction(filterCondition)
          ? filterCondition({ tab, segment, filter })
          : filterCondition) || {};
      const { granularity, date } = filterValue || {};
      const [startDate, endDate] = date || [];
      const validGranularity = getValidGranularity(granularity || 'quarter', granularities);
      const [minDate, maxDate] = ranges?.[validGranularity];
      const [defaultStartDate, defaultEndDate] = defaultDates?.[validGranularity];
      const { startDate: validStartDate, endDate: validEndDate } = getValidDateRange({
        granularity: validGranularity,
        granularities,
        startDate: startDate || defaultStartDate,
        endDate: endDate || defaultEndDate,
        minDate,
        maxDate,
      });
      result[key] = {
        ...filterValue,
        granularity: validGranularity,
        date: [validStartDate, validEndDate],
      };
    } else if (key === 'dueDate') {
      const { min_date: minDate, max_date: maxDate } = filterCondition || {};
      const { startDate, endDate } = filterValue || {};
      const { startDate: validStartDate, endDate: validEndDate } = getValidDateRange({
        startDate,
        endDate,
        minDate,
        maxDate,
      });
      result[key] = {
        ...filterValue,
        startDate: validStartDate || '',
        endDate: validEndDate || '',
      };
    } else if (key === 'rangePicker') {
      const { min_date: minDate, max_date: maxDate } = filterCondition || {};
      const {
        dateType: defaultDateType,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        typeList: defaultTypeList,
      } = filterDefaultValue || {};

      const { dateType, startDate, endDate } = filterValue || {};
      const dateTypeTmp = dateType || defaultDateType;
      const startDateTmp = startDate || defaultStartDate;
      const endDateTmp = endDate || defaultEndDate;
      const typeListTemp = defaultTypeList || [];
      const validDateType = getValidGranularity(dateTypeTmp, typeListTemp);
      const { startDate: validStartDate, endDate: validEndDate } = getValidDateRange({
        granularity: validDateType,
        startDate: startDateTmp,
        endDate: endDateTmp,
        minDate,
        maxDate,
      });
      result[key] = {
        ...filterValue,
        dateType: validDateType,
        startDate: validStartDate,
        endDate: validEndDate,
      };
    } else if (key === 'project_id') {
      result[key] = getValidListValue({
        values: filterValue,
        defaultValues: filterDefaultValue,
        allValues: allFilterOptionsValue,
      });
    } else if (Array.isArray(filterDefaultValue)) {
      const options: IGPSelectOptionWithGroup[] = isFunction(filterCondition)
        ? filterCondition({ tab, segment, filter })
        : filterCondition;
      result[key] = getValidListValue({
        values: filterValue,
        defaultValues: filterDefaultValue,
        allValues: options?.map((item) => item?.value) || [],
      });
    }
  });
  return result;
};

export default function usePageFilterInit(props: {
  loading: boolean;
  conditions: IConditions;
  storeValues: IStoreValues;
  defaultValues: IValues;
  allFilterOptionsValues: any;
  fields?: any;
  setSelectedValue?: any;
}) {
  const {
    loading: loadingProps,
    conditions,
    storeValues,
    defaultValues,
    allFilterOptionsValues,
    fields,
    setSelectedValue,
  } = props;

  const {
    lock,
    tab: storeTab,
    segment: storeSegment,
    filter: storeFilter,
    ...restStoreValues
  } = storeValues || {};

  const {
    tab: defaultTab,
    segment: defaultSegment,
    filter: defaultFilter,
    ...restDefaultValues
  } = defaultValues || {};

  const { short_url_key: shortUrlParamsKey } = useSearchParams();
  const storageValues = getFilterStorageValue();
  const saveValues = getFilterSaveValue() || {};
  const saveSelectedValues = getFilterSaveSelectedValue() || {};
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(lock ? storeTab : defaultTab);
  const [segment, setSegment] = useState(lock ? storeSegment : defaultSegment);
  const [filter, setFilter] = useState<IRecord>(lock ? storeFilter : defaultFilter);
  const [data, setData] = useState<IRecord>(lock ? restStoreValues : restDefaultValues);

  const initByShortUrlParamsKey = async (params: {
    shortUrlParamsKey: string;
    conditions: IConditions;
    defaultValues: IValues;
  }) => {
    const { shortUrlParamsKey, conditions, defaultValues } = params || {};
    const {
      tab: tabConditions,
      segment: segmentConditions,
      filter: filterConditions,
    } = conditions || {};
    const {
      tab: tabDefaultValues,
      segment: segmentDefaultValues,
      filter: filterDefaultValues,
    } = defaultValues || {};

    try {
      const res = await fetchShortUrlParams({ key: shortUrlParamsKey });
      const { params } = res?.data || {};
      const { tab, segment, filter, data } = deserializeShortUrlParams(params);

      const validTab = getValidValue({
        value: tab,
        defaultValue: tabDefaultValues,
        allValues: tabConditions,
      });
      const validSegment = getValidValue({
        value: segment,
        defaultValue: segmentDefaultValues,
        allValues: segmentConditions,
      });
      const validFilter = getValidFilter({
        tab: validTab,
        segment: validSegment,
        filter,
        filterConditions,
        filterDefaultValues,
        allFilterOptionsValues,
      });
      setTab(validTab);
      setSegment(validSegment);
      setFilter(validFilter);
      setData(data || {});
    } catch (exception) {
      console.warn(exception);
    }
  };

  const handleUpdateShortUrlParams = async (params: IShortUrlParams) => {
    try {
      const res = await updateShortUrlParams({ params: JSON.stringify(params) });
      const shortUrlKey = res?.data?.key || '';
      updateURLSearchParams({ short_url_key: shortUrlKey });
    } catch (exception) {}
  };

  // 因时间筛选器不继承，继承时过滤时间筛选器值
  const filterTimeValue = (filterValue: any) => {
    const valueTemp = filterValue || {};
    delete valueTemp?.rangePicker;
    delete valueTemp?.dueDate;
    delete valueTemp?.composedDatePicker;
    return valueTemp;
  };

  const initByStorage = async (storageValues: any) => {
    const {
      tab: tabConditions,
      segment: segmentConditions,
      filter: filterConditions,
    } = conditions || {};
    const {
      tab: tabDefaultValues,
      segment: segmentDefaultValues,
      filter: filterDefaultValues,
    } = defaultValues || {};
    const validTab = getValidValue({
      value: '',
      defaultValue: tabDefaultValues,
      allValues: tabConditions,
    });
    const validSegment = getValidValue({
      value: '',
      defaultValue: segmentDefaultValues,
      allValues: segmentConditions,
    });
    // 继承能继承的
    const validFilter = getValidFilter({
      tab: validTab,
      segment: validSegment,
      filter: filterTimeValue(storageValues),
      filterConditions,
      filterDefaultValues,
      allFilterOptionsValues,
    });
    setTab(validTab);
    setSegment(validSegment);
    setFilter(validFilter);
    setData({});
  };
  const initBySave = async (saveValues: any) => {
    const {
      tab: tabConditions,
      segment: segmentConditions,
      filter: filterConditions,
    } = conditions || {};
    const {
      tab: tabDefaultValues,
      segment: segmentDefaultValues,
      filter: filterDefaultValues,
    } = defaultValues || {};
    const validTab = getValidValue({
      value: '',
      defaultValue: tabDefaultValues,
      allValues: tabConditions,
    });
    const validSegment = getValidValue({
      value: '',
      defaultValue: segmentDefaultValues,
      allValues: segmentConditions,
    });
    // 继承能继承的
    const validFilter = getValidFilter({
      tab: validTab,
      segment: validSegment,
      filter: filterTimeValue(saveValues),
      filterConditions,
      filterDefaultValues,
      allFilterOptionsValues,
    });
    setTab(validTab);
    setSegment(validSegment);
    setFilter(validFilter);
    setSelectedValue?.(saveSelectedValues);
    setData({});
  };

  useAsyncEffect(async () => {
    if (loadingProps) return;
    if (!!shortUrlParamsKey) {
      await initByShortUrlParamsKey({
        shortUrlParamsKey,
        conditions,
        defaultValues,
      });
    } else if (!isEmpty(saveValues) && !isEmpty(saveSelectedValues)) {
      const saveValueFilter: any = Array.isArray(fields)
        ? getSaveValue(defaultFilter, saveValues, fields)
        : saveValues;
      await initBySave(saveValueFilter);
      // await initByStore({
      //   storeValues,
      //   conditions,
      //   defaultValues,
      // });
    } else if (!isEmpty(storageValues)) {
      const saveValueFilter = Array.isArray(fields)
        ? getSaveValue(defaultFilter, storageValues, fields)
        : storageValues;
      await initByStorage(saveValueFilter);
      // await initByStore({
      //   storeValues,
      //   conditions,
      //   defaultValues,
      // });
    } else {
      const {
        tab: tabConditions,
        segment: segmentConditions,
        filter: filterConditions,
      } = conditions || {};
      const {
        tab: tabDefaultValues,
        segment: segmentDefaultValues,
        filter: filterDefaultValues,
      } = defaultValues || {};
      const validTab = getValidValue({
        value: '',
        defaultValue: tabDefaultValues,
        allValues: tabConditions,
      });
      const validSegment = getValidValue({
        value: '',
        defaultValue: segmentDefaultValues,
        allValues: segmentConditions,
      });
      const validFilter = getValidFilter({
        tab: validTab,
        segment: validSegment,
        filter: {},
        filterConditions,
        filterDefaultValues,
        allFilterOptionsValues,
      });
      setTab(validTab);
      setSegment(validSegment);
      setFilter(validFilter);
      setData({});
    }
    setLoading(false);
  }, [loadingProps]);

  return {
    loading,
    tab,
    segment,
    filter,
    data,
    onUpdateShortUrlParams: handleUpdateShortUrlParams,
  };
}
